// ตัวช่วยของต้นไม้ Section / รายการ ของเทมเพลต PM Checklist (ไม่มี UI)
// node = { id, kind: "section" | "item", uid, ... }
//  - section: name, open, children[]
//  - item: description, input_type, orig_type, unit, min, max (string), is_required, is_active, label_left, label_right (string; "" = ค่าเริ่มต้น)
// ใบ Duplicate ใช้ cloneTree() คัดลอกทั้งต้นไม้ (id ใหม่ ไม่มี uid) แล้ว toPayload() ได้เลย

import { t } from "../../../components/CustomComponent.js";

export const LABEL_MAX = 12;

// ชนิดที่แสดงใน dropdown: check/yesno = Toggle (3), checkbox = Toggle (2)
export const TYPE_VALUES = ["check", "checkbox", "measurement", "text", "qty"];

// ค่าเริ่มต้นของป้ายซ้าย/ขวา (เก็บลง DB ตามนี้เมื่อช่องว่าง)
export const DEFAULT_LABELS = {
  toggle3: ["ไม่ผ่าน", "ผ่าน"],
  toggle2: ["ไม่ได้ทำ", "ทำแล้ว"],
};

export const typeLabel = (value) =>
  ({
    check: t("ct_type_toggle3"),
    yesno: t("ct_type_toggle3"),
    checkbox: t("ct_type_toggle2"),
    measurement: t("ct_type_measurement"),
    text: t("ct_type_text"),
    qty: t("ct_type_qty"),
  }[value] || value);

export const isToggle3 = (type) => type === "check" || type === "yesno";
export const isToggle2 = (type) => type === "checkbox";
export const isToggle = (type) => isToggle3(type) || isToggle2(type);
export const hasUnit = (type) => type === "measurement" || type === "qty";
// ชนิดที่มีช่องตั้งค่าเพิ่ม (ใช้จัด layout)
export const hasExtra = (type) => isToggle(type) || hasUnit(type);
// ค่าที่ dropdown ใช้ (yesno เดิมแสดงเป็น Toggle 3 แต่ค่าที่เก็บยังเป็น yesno จนกว่าผู้ใช้จะเปลี่ยนชนิดเอง)
export const selectValue = (type) => (type === "yesno" ? "check" : type || "check");
export const defaultLabels = (type) => (isToggle2(type) ? DEFAULT_LABELS.toggle2 : isToggle3(type) ? DEFAULT_LABELS.toggle3 : ["", ""]);

let idCounter = 0;
export const newId = () => `n${Date.now().toString(36)}${++idCounter}`;

export const createItem = (over = {}) => ({
  id: newId(),
  kind: "item",
  uid: "",
  description: "",
  input_type: "check",
  orig_type: "",
  unit: "",
  min: "",
  max: "",
  is_required: true,
  is_active: true,
  label_left: "",
  label_right: "",
  ...over,
});

export const createSection = (over = {}) => ({
  id: newId(),
  kind: "section",
  uid: "",
  name: "",
  open: true,
  children: [],
  ...over,
});

export const countItems = (list) => list.reduce((n, x) => n + (x.kind === "item" ? 1 : countItems(x.children)), 0);
export const countSections = (list) => list.reduce((n, x) => n + (x.kind === "section" ? 1 + countSections(x.children) : 0), 0);

export const summarize = (tree) => {
  const items = [];
  const walk = (list) => list.forEach((x) => (x.kind === "item" ? items.push(x) : walk(x.children)));
  walk(tree);
  const required = items.filter((i) => i.is_required).length;
  return { sections: countSections(tree), items: items.length, required, optional: items.length - required };
};

export const locate = (tree, id, list = tree, parent = null) => {
  for (let i = 0; i < list.length; i += 1) {
    const node = list[i];
    if (node.id === id) return { node, list, index: i, parent };
    if (node.kind === "section") {
      const found = locate(tree, id, node.children, node);
      if (found) return found;
    }
  }
  return null;
};

// list อยู่ใน sec หรือลูกหลานของ sec ไหม (กันวาง Section ลงในตัวเอง)
export const ownsList = (sec, list) => {
  if (sec.children === list) return true;
  return sec.children.some((c) => c.kind === "section" && ownsList(c, list));
};

// ย้าย node ไปแทรกที่ list[index] (index นับก่อนถอดตัวเองออก) คืน true ถ้าตำแหน่งเปลี่ยนจริง
export const moveTo = (tree, id, list, index) => {
  const src = locate(tree, id);
  if (!src) return false;
  if (src.node.kind === "section" && ownsList(src.node, list)) return false;
  if (src.list === list) {
    if (index === src.index || index === src.index + 1) return false;
    if (src.index < index) index -= 1;
  }
  src.list.splice(src.index, 1);
  list.splice(index, 0, src.node);
  return true;
};

export const canMove = (tree, id) => {
  const p = locate(tree, id);
  if (!p) return {};
  const prev = p.list[p.index - 1];
  return {
    up: p.index > 0 || !!p.parent,
    down: p.index < p.list.length - 1 || !!p.parent,
    right: !!(prev && prev.kind === "section"),
    left: !!p.parent,
  };
};

// ย้ายด้วยลูกศร (โหมดคลิก ⋮⋮) คืน true ถ้าย้ายสำเร็จ
export const moveByDirection = (tree, id, dir) => {
  const p = locate(tree, id);
  if (!p) return false;
  if (dir === "up") {
    if (p.index > 0) return moveTo(tree, id, p.list, p.index - 1);
    if (p.parent) {
      const g = locate(tree, p.parent.id);
      return moveTo(tree, id, g.list, g.index);
    }
  } else if (dir === "down") {
    if (p.index < p.list.length - 1) return moveTo(tree, id, p.list, p.index + 2);
    if (p.parent) {
      const g = locate(tree, p.parent.id);
      return moveTo(tree, id, g.list, g.index + 1);
    }
  } else if (dir === "right") {
    const prev = p.list[p.index - 1];
    if (prev && prev.kind === "section") return moveTo(tree, id, prev.children, prev.children.length);
  } else if (dir === "left") {
    if (p.parent) {
      const g = locate(tree, p.parent.id);
      return moveTo(tree, id, g.list, g.index + 1);
    }
  }
  return false;
};

// ---- เลขข้อความ ----
const toText = (v) => (v === null || v === undefined ? "" : String(v));

// ---- API response -> tree ----
// ลำดับพี่น้อง: sequence จากเซิร์ฟเวอร์ (เลข DFS ทั้งเทมเพลต) เท่ากัน = รายการก่อน Section
export const buildTree = (sections = [], items = []) => {
  const secNodes = new Map();
  (sections || []).forEach((s) => {
    secNodes.set(s.uid, createSection({ id: s.uid, uid: s.uid, name: s.name || "", _seq: s.sequence || 0, _parent: s.parent_uid || null }));
  });
  const root = [];
  const bucket = (parentUid) => (parentUid && secNodes.has(parentUid) ? secNodes.get(parentUid).children : root);

  const entries = [];
  secNodes.forEach((node) => entries.push({ node, seq: node._seq, rank: 1, parent: node._parent }));
  (items || []).forEach((i) => {
    const node = createItem({
      id: i.uid || newId(),
      uid: i.uid || "",
      description: i.description || "",
      input_type: i.input_type || "check",
      orig_type: i.input_type || "check",
      unit: toText(i.unit),
      min: toText(i.min),
      max: toText(i.max),
      is_required: !!i.is_required,
      is_active: i.is_active !== false,
    });
    // ป้ายที่เท่ากับค่าเริ่มต้นของชนิดนั้น แสดงเป็นช่องว่าง (ว่าง = ค่าเริ่มต้น)
    const [dl, dr] = defaultLabels(node.input_type);
    node.label_left = toText(i.label_left) === dl ? "" : toText(i.label_left);
    node.label_right = toText(i.label_right) === dr ? "" : toText(i.label_right);
    entries.push({ node, seq: i.sequence || 0, rank: 0, parent: i.section_uid || null });
  });
  entries
    .map((e, order) => ({ ...e, order }))
    .sort((a, b) => a.seq - b.seq || a.rank - b.rank || a.order - b.order)
    .forEach((e) => bucket(e.parent).push(e.node));
  secNodes.forEach((node) => {
    delete node._seq;
    delete node._parent;
  });
  return root;
};

// ---- tree -> payload แบน (sections + items) ----
// sequence = ลำดับในกลุ่มพี่น้องเดียวกัน (รวมรายการและ Section) เซิร์ฟเวอร์เลขใหม่เป็น DFS เอง
const numOrNull = (v) => {
  const s = String(v ?? "").trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export const toPayload = (tree) => {
  const sections = [];
  const items = [];
  const walk = (list, parentRef) => {
    list.forEach((node, index) => {
      const sequence = index + 1;
      if (node.kind === "section") {
        const ref = node.uid || `sec_${node.id}`;
        sections.push({
          ...(node.uid ? { uid: node.uid } : { client_key: ref }),
          parent_uid: parentRef,
          name: String(node.name || "").trim(),
          sequence,
        });
        walk(node.children, ref);
        return;
      }
      const type = node.input_type || "check";
      const [dl, dr] = defaultLabels(type);
      items.push({
        ...(node.uid ? { uid: node.uid } : {}),
        sequence,
        description: node.description || "",
        input_type: type,
        unit: hasUnit(type) ? String(node.unit || "").trim() : "",
        min: type === "measurement" ? numOrNull(node.min) : null,
        max: type === "measurement" ? numOrNull(node.max) : null,
        is_required: !!node.is_required,
        is_active: node.is_active !== false,
        label_left: isToggle(type) ? String(node.label_left || "").trim() || dl : null,
        label_right: isToggle(type) ? String(node.label_right || "").trim() || dr : null,
        section_uid: parentRef,
      });
    });
  };
  walk(tree, null);
  return { sections, items };
};

// คัดลอกทั้งต้นไม้เป็นชุดใหม่ (ใช้กับปุ่ม Duplicate) - id ใหม่ ตัด uid ทิ้ง
export const cloneTree = (list) =>
  list.map((n) =>
    n.kind === "section"
      ? { ...n, id: newId(), uid: "", children: cloneTree(n.children) }
      : { ...n, id: newId(), uid: "", orig_type: "" }
  );

// ---- ตรวจก่อนบันทึก ----
export const isInvalidRange = (item) =>
  item.input_type === "measurement" &&
  String(item.min ?? "").trim() !== "" &&
  String(item.max ?? "").trim() !== "" &&
  Number(item.min) > Number(item.max);

// Unit ว่าง (ใช้ขึ้นกรอบแดง)
export const isUnitMissing = (item) => hasUnit(item.input_type) && !String(item.unit || "").trim();

// Unit ว่างแล้วบันทึกไม่ได้ เฉพาะรายการใหม่/เปลี่ยนชนิด (ตรงกับ backend) - รายการเดิมที่ชนิดไม่เปลี่ยนเปิดดู/บันทึกซ้ำได้
export const isUnitBlocking = (item) => isUnitMissing(item) && (!item.uid || item.orig_type !== item.input_type);

// คืน { type, node } ของปัญหาแรกที่เจอ (เรียงตามลำดับในหน้า) หรือ null
export const findProblem = (list, prefix = "") => {
  let k = 0;
  for (const node of list) {
    if (node.kind === "section") {
      k += 1;
      const num = `${prefix}${k}`;
      if (!String(node.name || "").trim()) return { type: "section_name", node, num };
      const inner = findProblem(node.children, `${num}.`);
      if (inner) return inner;
    } else {
      if (isInvalidRange(node)) return { type: "range", node };
      if (isUnitBlocking(node)) return { type: "unit", node };
    }
  }
  return null;
};

// ทศนิยมไม่เกิน 2 ตำแหน่ง
export const limitDecimals = (v) => (/\.\d{3,}$/.test(v) ? v.replace(/(\.\d{2})\d+$/, "$1") : v);
